import { ChangeEvent, useCallback, useRef, useState } from 'react';

const isValidEmailRegex =
  /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;

type EmailState = 'yes' | 'maybe' | 'no' | null;

export default function useEmailAutocomplete({
  defaultValue = '',
  validation = true,
} = {}) {
  const input = useRef<HTMLInputElement>();
  const email = useRef(defaultValue);
  const isValid = useRef<EmailState>(null);
  const [, forceUpdate] = useState(false);

  const validate = useCallback((nextEmail: string) => {
    const inputIsFocused = input.current === document.activeElement;

    if (!nextEmail) {
      return null;
    }
    if (isValidEmailRegex.test(nextEmail)) {
      return 'yes';
    }
    if (inputIsFocused) {
      return 'maybe';
    }
    return 'no';
  }, []);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      email.current = value;
      isValid.current = validate(value);
      forceUpdate((x) => !x);
    },
    [validate],
  );

  const doValidationCheck = useCallback(() => {
    if (validation) {
      isValid.current = validate(email.current);
      forceUpdate((x) => !x);
    }
    return email.current;
  }, [validation, validate]);

  const htmlAttributes = {
    value: email.current,
    onChange,
    ref: input,
    onBlur: doValidationCheck,
    onFocus: doValidationCheck,
  };

  return {
    email: new Proxy(
      { address: email, isValid },
      { get: (obj, key: 'address' | 'isValid') => obj[key].current },
    ),
    ...htmlAttributes,
    bind: htmlAttributes,
    isValid: isValid.current === 'yes',
  };
}
